<template>
  <div
    class="images"
    :class="[
      $blockClass(blockClass, 'image-container'),
      { 'images--no-padding': !padding },
    ]"
  >
    <span
      v-if="padding"
      class="images__spacer"
      :class="[
        $blockClass(blockClass, 'spacer'),
        { 'images__spacer--mobile': ratioDesktop }
      ]"
      :style="{ paddingBottom: getAspectRatio() }"
      aria-hidden="true"
    />

    <span
      v-if="padding && ratioDesktop"
      class="images__spacer images__spacer--desktop"
      :class="$blockClass(blockClass, 'spacer')"
      :style="{ paddingBottom: getAspectRatio(true) }"
      aria-hidden="true"
    />

    <picture
      v-if="sources.length"
      class="images__picture"
      :class="$blockClass(blockClass, 'picture')"
    >
      <source
        v-for="(source) of sources"
        :key="`source-${source.breakpoint}`"
        class="images__image"
        :class="$blockClass(blockClass, 'image')"
        :media="source.media"
        :data-srcset="source.src"
      >

      <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
      <img
        v-if="image.src || image.url"
        class="images__image"
        :class="[
          $blockClass(blockClass, 'image'),
          { 'lazyload': lazyload },
        ]"
        :alt="imageAlt"
        decoding="async"
        fetchpriority="high"
        :role="role"
        :src="getFallbackSrc(true)"
        :data-src="getFallbackSrc(false)"
      >
    </picture>
  </div>
</template>

<script>

/**
 * Vue: Art direction
 * -----------------------------------------------------------------------------
 * Base template for art direction image.
 * - Designed for images which change based on breakpoint.
 * - Stylesheet imported in responsive image component.
 * - image.src comes from formatted product response.
 * - image.url comes from unformatted cart.js response.
 *
 * @param {String} [alt] - Override image alt.
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {String} [breakpointSizes] - Image size to use at each breakpoint in
 * the format [xs]-[s]-[m]-[l]-[xl].
 * @param {Object} image - Image object formatted from Shopify response.
 * @param {Object} [imageDesktop] - Override image object for desktop.
 * @param {Boolean} [lazyload] - Lazyload image, defaults to true.
 * @param {Boolean} [padding] - Add padding based on image ratio or supplied
 * ratio, default to true, disable to make image fill its parent container.
 * @param {String} [ratio] - Aspect ratio of image in `width:height` format or
 * in decimal ratio format (width/height), will use original ratio of image if
 * not set.
 * @param {String} [ratioDesktop] - Override image ratio for desktop.
 *
 */
import { mapState } from 'vuex'

import { values as breakpoints } from '~/config/breakpoints'

export default {
  name: 'ArtDirection',

  props: {
    alt: {
      type: String,
      default: '',
    },
    blockClass: {
      type: String,
      default: '',
    },
    breakpointSizes: {
      type: [Boolean, String],
      default: false,
      validator: (value) => {
        return (
          value === false ||
          value.split('-').length === Object.keys(breakpoints).length
        )
      },
    },
    image: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    imageDesktop: {
      type: Object,
      default: () => ({}),
    },
    lazyload: {
      type: Boolean,
      default: true,
    },
    padding: {
      type: Boolean,
      default: true,
    },
    ratio: {
      type: String,
      default: '',
    },
    ratioDesktop: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      global: true,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      mobileBreakpoints: (state) => state.index.settings.mobileBreakpoints,
    }),

    /**
     * Compute alt.
     * @returns {String}
     */
    imageAlt() {
      const imageAlt = this.alt || this.image.alt
      return imageAlt ? imageAlt : ''
    },

    /**
     * Compute role.
     * @returns {String|Null}
     */
    role() {
      if (this.imageAlt) {
        return null
      }

      return 'presentation'
    },

    /**
     * Compute images array for <source> elements.
     * @returns {Array}
     */
    sources() {
      const breakpointKeys = Object.keys(breakpoints)

      return breakpointKeys.map((key, index) => {
        let imageObject = this.image
        const lastBreakpoint = index === (breakpointKeys.length - 1)
        let mediaQuerySize = 0

        /**
         * If not a mobile breakpoint then use desktop image (if it exists).
         */
        if (
          (this.imageDesktop.src || this.imageDesktop.url) &&
          !this.mobileBreakpoints.includes(key)
        ) {
          imageObject = this.imageDesktop
        }

        /**
         * Early return if image object is missing image.
         */
        if (!imageObject.src && !imageObject.url) {
          return false
        }

        /**
         * Use next breakpoint size to determine max-width and image size.
         * - Unless it's the last breakpoint.
         */
        if (!lastBreakpoint) {
          const nextBreakpointKey = breakpointKeys[index + 1]
          mediaQuerySize = breakpoints[nextBreakpointKey]
        }

        let size = mediaQuerySize || '1024'

        if (lastBreakpoint) {
          size = imageObject.width || '2048'
        }

        if (this.breakpointSizes) {
          size = Number(this.breakpointSizes.split('-')[index])
        }

        return {
          breakpoint: key,
          media: lastBreakpoint ? '' : `(max-width: ${mediaQuerySize}px)`,
          size,
          src: this.$formatImageUrl(imageObject.src || imageObject.url, { width: size }),
        }
      }).filter(Boolean)
    },
  },

  methods: {

    /**
     * Get aspect ratio.
     * @param {Boolean} desktop - Desktop breakpoint?
     * @returns {Number|Null}
     */
    getAspectRatio(desktop) {
      if (!this.padding) {
        return null
      }

      const ratio = desktop ? this.ratioDesktop : this.ratio

      const imageObject = this.imageDesktop.src || this.imageDesktop.url
        ? this.imageDesktop
        : this.image

      /**
       * If ratio is supplied as a decimal ratio.
       */
      if (ratio && ratio.includes('.')) {
        return `${(1 / ratio) * 100.00}%`
      }

      /**
       * If no ratio supplied and image has width / height ratio in object.
       */
      if (!ratio && imageObject.aspect_ratio) {
        return `${(1 / imageObject.aspect_ratio) * 100.00}%`
      }

      let height = imageObject.height
      let width = imageObject.width / 100.00

      /**
       * If ratio is passed as aspect ratio.
       */
      if (ratio && ratio.includes(':')) {
        height = ratio.split(':')[1] * 100.00
        width = ratio.split(':')[0]
      }

      return `${height / width}%`
    },

    /**
     * Get fallback src URL.
     * - Can either be src or data-src based on lazyload.
     * @param {Boolean} dataAttribute - Is data- attribute.
     * @returns {String|Null}
     */
    getFallbackSrc(dataAttribute) {
      if (
        (!this.image.src && !this.image.url) ||
        (this.lazyload && !dataAttribute)
      ) {
        return null
      }

      return this.$formatImageUrl(this.image.src || this.image.url, { width: 100 })
    },
  },
}
</script>
